import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Chip, Divider, makeStyles, TextField, Typography } from '@material-ui/core';
import { Controller, useForm } from 'react-hook-form';
import { DeleteOutline } from '@material-ui/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { colors } from 'theme';
import CollapsableCard from 'components/Generic/CollapsableCard';
import { Material } from 'types';
import { useMaterials } from 'hooks';
import SelectWithErrorField from 'components/CustomInputs/SelectWithErrorField';
import { CancelIcon, ValidateIcon } from 'components/Icons';
import { AppContext } from 'context';
import { streamCreateSchema } from './StreamCreateForm.yup';
import { DEFAULT_ITEMS_PER_SELECT } from '../../../tableFilterContext';

const useStyles = makeStyles({
  divider: {
    margin: '10px 34px 10px 11px',
    height: '60%',
  },
  streamName: {
    width: '30%',
  },
  collapsableDivider: {
    margin: '10px 0 8px 0',
  },
  material: {
    marginLeft: '24px',
    display: 'flex',
    alignItems: 'center',
  },
  materialsList: {
    marginLeft: '19px',
  },
  selectInput: {
    padding: '7px 0px 0px 9px',
    marginRight: 10,
    height: 31,
    display: 'flex',
    flexAlign: 'center',
  },
  materialLabel: {
    fontWeight: 600,
    fontSize: 14,
    marginRight: 33,
  },
  actions: {
    flexGrow: 1,
    textAlign: 'right',
  },
  actionIcon: {
    width: 15,
    height: 15,
  },
  actionButton: {
    padding: 5,
    margin: 0,
    marginRight: 15,
    minWidth: 10,
  },
});

type StreamCommand = {
  name: string;
  sortingCenter?: string;
  composition: string[];
};

type StreamCreateFormProps = {
  onCancel: () => void;
  onSubmit: (stream: StreamCommand) => void;
};

const StreamCreateForm: React.FC<StreamCreateFormProps> = ({ onCancel, onSubmit }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const materials = useMaterials({ itemsPerPage: String(DEFAULT_ITEMS_PER_SELECT) });
  const [materialOptions, setMaterialOptions] = useState<Material[]>([]);
  const [streamComposition, setStreamComposition] = useState<Material[]>([]);
  const [reject, setReject] = useState<Material | null>(null);

  const schema = useMemo(() => streamCreateSchema(t), [t]);

  const context = useContext(AppContext);
  const sortingCenter = context.getSortingCenter?.();

  useEffect(() => {
    if (!materials) {
      setMaterialOptions([]);
      setReject(null);
      return;
    }
    setReject(materials.find(material => material.isReject) ?? null);
    setMaterialOptions(materials.filter(material => !streamComposition.includes(material) && !material.isReject));
  }, [materials, streamComposition, setMaterialOptions, setReject]);

  const addMaterial = useCallback(
    (materialId: string) => {
      if (!materials) return;
      const selectedMaterial = materials.find(material => material['@id'] === materialId);
      if (!selectedMaterial) return;
      setStreamComposition(
        [...streamComposition, selectedMaterial].sort((a, b) => a.shortName.localeCompare(b.shortName)),
      );
    },
    [materials, streamComposition, setStreamComposition],
  );

  const removeMaterial = useCallback(
    (materialToRemove: Material) => {
      setStreamComposition(streamComposition.filter(material => material !== materialToRemove));
    },
    [streamComposition, setStreamComposition],
  );

  const { control, handleSubmit, setValue } = useForm({
    mode: 'onChange',
    defaultValues: {
      name: '',
      material: '',
    },
    resolver: yupResolver(schema),
  });

  const generateStreamAndSubmit = useCallback(
    ({ name }: { name: string }) => {
      onSubmit({
        name,
        composition: (reject !== null ? [...streamComposition, reject] : streamComposition).map(
          material => material['@id'],
        ),
        sortingCenter: sortingCenter?.['@id'],
      });
    },
    [streamComposition, onSubmit, reject, sortingCenter],
  );

  return (
    <form onSubmit={handleSubmit(generateStreamAndSubmit)}>
      <CollapsableCard
        defaultExpanded
        collapsableContent={
          <>
            <Typography variant="inherit" className={classes.material}>
              <div className={classes.materialLabel}>{t('materials.entityName')}</div>
              <Controller
                name="material"
                control={control}
                render={({ field: { ref, onChange, ...inputProps }, fieldState }) => (
                  <SelectWithErrorField
                    id="material"
                    choices={materialOptions}
                    choiceDisplayKey="shortName"
                    error={fieldState.invalid}
                    errorMessage={fieldState.error?.message}
                    placeholder={t('streams.create.materialChoice')}
                    inputRef={ref}
                    classes={{ root: classes.selectInput }}
                    disabled={materialOptions.length === 0}
                    onChange={e => {
                      onChange(e);
                      setValue('material', '');
                      addMaterial(e.target.value as string);
                    }}
                    {...inputProps}
                  />
                )}
              />
              <div className={classes.actions}>
                <Button classes={{ root: classes.actionButton }} onClick={onCancel}>
                  <CancelIcon className={classes.actionIcon} />
                </Button>
                <Button type="submit" className={classes.actionButton}>
                  <ValidateIcon className={classes.actionIcon} />
                </Button>
              </div>
            </Typography>
            <Divider className={classes.collapsableDivider} />
            <Box className={classes.materialsList}>
              {streamComposition.map(material => (
                <Chip
                  key={material['@id']}
                  label={material.shortName}
                  onDelete={() => {
                    removeMaterial(material);
                  }}
                  deleteIcon={<DeleteOutline style={{ color: colors.white }} />}
                />
              ))}
              {reject !== null && <Chip label={reject.shortName} />}
            </Box>
          </>
        }
      >
        <Typography variant="inherit" className={classes.streamName}>
          <Controller
            name="name"
            control={control}
            render={({ field: { ref, ...inputProps }, fieldState }) => (
              <TextField
                fullWidth
                id="name"
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
                inputRef={ref}
                onClick={e => {
                  e.stopPropagation();
                }}
                {...inputProps}
              />
            )}
          />
        </Typography>
        <Divider className={classes.divider} orientation="vertical" flexItem />
        <Typography variant="inherit" color="primary">
          {streamComposition.length > 0
            ? t('streams.material_other', { count: streamComposition.length + 1 })
            : t('streams.material_one', { count: streamComposition.length + 1 })}
        </Typography>
      </CollapsableCard>
    </form>
  );
};

export default StreamCreateForm;
